import { Helmet } from 'react-helmet'

import { StatusPill } from '@/components/StatusPill'
import { CopyButton } from '@/components/ui/CopyButton'
import { OrgStatus } from '@/models/user'
import { Cap, Demo, PhonePreview } from '../../primitives'
import { PrototypeFrame } from '../../PrototypeFrame'
import { AccountBanner, AccountStage } from './AccountBanner'

interface ConsoleStage {
  label: string
  /** Absent when the Console has no such stage yet — the pill then renders as proposed. */
  status?: OrgStatus
}

/** The real pill for a stage that exists; a dashed outline for one that does not. */
function ConsoleStagePill({ stage }: { stage: ConsoleStage }) {
  if (stage.status) {
    return <StatusPill status={stage.status} className="px-2 py-0.5 text-[10px]" />
  }
  return (
    <span className="border-ink-300 text-ink-500 inline-block rounded-sm border border-dashed px-2 py-0.5 text-[10px] font-medium">
      {stage.label}
    </span>
  )
}

interface StepDemo {
  key: string
  label: string
  /** Whether this banner's copy ships today — the step indicator is new on all six. */
  shipped: boolean
  /** What a staff member sees on the org in the Console while this banner is up. */
  consoleStage: ConsoleStage
  /** What actually puts the banner in this state — not always the Console status. */
  trigger: string
  stage: AccountStage
}

// The banner is 200px plus a full-width CTA once the row wraps at phone width.
const MOBILE_FRAME_HEIGHT = 360

/** Journey order, so the page reads as the sequence a customer actually walks. */
const STEPS: StepDemo[] = [
  {
    key: 'application',
    label: 'Step 1 · Application in progress',
    shipped: false,
    consoleStage: { label: 'Onboarding', status: OrgStatus.ONBOARDING },
    trigger: 'no dispatched indications',
    stage: AccountStage.APPLICATION_INCOMPLETE,
  },
  {
    key: 'quotes',
    label: 'Step 2 · Quotes ready',
    shipped: false,
    consoleStage: { label: 'Quoting', status: OrgStatus.QUOTING },
    trigger: 'hasDispatchedIndications && !hasAppliedIndications',
    stage: AccountStage.QUOTES_READY,
  },
  {
    key: 'checkout',
    label: 'Step 3 · Checkout started',
    shipped: false,
    consoleStage: { label: 'Quoting', status: OrgStatus.QUOTING },
    trigger: 'cart has coverages && !hasAppliedIndications — needs the cart persisted',
    stage: AccountStage.CHECKOUT_STARTED,
  },
  {
    key: 'with-broker',
    label: 'Step 4 · Checkout requested, in underwriting',
    shipped: false,
    consoleStage: { label: 'Underwriting', status: OrgStatus.UNDERWRITING },
    trigger: 'hasAppliedIndications — read off the application, not the status',
    stage: AccountStage.WITH_BROKER,
  },
  {
    key: 'checking-out',
    label: 'Step 5 · Checking out',
    shipped: false,
    consoleStage: { label: 'Checking out' },
    trigger: 'no Console stage exists — needs one between UNDERWRITING and ACTIVE',
    stage: AccountStage.CHECKING_OUT,
  },
  {
    key: 'active',
    label: 'Step 6 · All coverages active',
    shipped: false,
    consoleStage: { label: 'Active', status: OrgStatus.ACTIVE },
    trigger: 'nothing runs here — showsOnboardingBanner is false once ACTIVE',
    stage: AccountStage.ALL_ACTIVE,
  },
]

const BRIEF = (
  <div className="border-ink-700 grid grid-cols-1 gap-6 border-t pt-6 sm:grid-cols-2 lg:grid-cols-4">
    <div>
      <Cap onDark>Goal</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        Put every account banner a customer can meet on one page, in journey order, each tagged with the stage
        a staff member sees in the Console at that moment, and each carrying a five-step indicator so the
        nudge says how far along the purchase is. Two pairs of stages share a step.
      </p>
    </div>

    <div>
      <Cap onDark>Project: Sub-5 self-serve</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        The account banner is the only thing pulling a customer back into an unfinished purchase. A wrong or
        missing nudge is a dropped self-serve sale between application and bind.
      </p>
    </div>

    <div>
      <Cap onDark>What is real</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        Nothing on this page ships as shown — every step&rsquo;s copy is proposed, and so are the step
        indicator, the artwork and the grid. These render the prototype&rsquo;s own banner, not the live{' '}
        <code>OnboardingBanner</code>. A dashed Console pill means that stage does not exist either; only Step
        5 is in that position.
      </p>
    </div>

    <div>
      <Cap onDark>Next</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        Settle whether the indicator earns its place, then three separate asks: persist the cart so Step 3 can
        fire, add the Checking out stage Step 5 keys off, and open the <code>showsOnboardingBanner</code> gate
        so Step 6 can run at all.
      </p>
    </div>
  </div>
)

export function AccountBannersPrototypePage() {
  return (
    <PrototypeFrame
      title="Account banners V1.0"
      owner="Carrie Phillips"
      date="September 11, 2026"
      status="Front-end prototype"
      brief={BRIEF}
    >
      <Helmet>
        <title>Prototype: Account banners V1.0</title>
      </Helmet>
      <div className="flex flex-col gap-10">
        {STEPS.map((step) => (
          <Demo
            key={step.key}
            label={
              <span className="inline-flex items-center gap-1">
                {step.label} — {step.shipped ? 'in production' : 'proposed'}
                <CopyButton
                  value={`<AccountBanner stage={AccountStage.${step.stage}} />`}
                  className="text-ink-400 hover:text-ink size-4 bg-transparent hover:bg-transparent"
                />
              </span>
            }
          >
            <AccountBanner stage={step.stage} />
            <div className="text-ink-400 mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[10px]">
              <Cap>Console</Cap>
              <ConsoleStagePill stage={step.consoleStage} />
              <span>{step.trigger}</span>
            </div>
            <div className="mt-6">
              <Cap>Mobile · 375px</Cap>
              <div className="mt-3">
                <PhonePreview
                  src={`/design-system/frames/account-banner?stage=${step.stage}`}
                  title={`${step.label} at 375px`}
                  height={MOBILE_FRAME_HEIGHT}
                />
              </div>
            </div>
          </Demo>
        ))}
      </div>
    </PrototypeFrame>
  )
}
